import React, { useEffect, useState } from "react";
import "../orderList/orderList.css";
import { userRequest } from "../../requestMethods";
import { useSelector } from "react-redux";
import moment from "moment";
import {
    Visibility as Eye,
    Close,
} from "@material-ui/icons";

const InstallmentRequests = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const superadmin = currentUser?.isSuperAdmin;
    const outright = currentUser?.type === "outright";
    const manager = currentUser?.type === "manager";
    const isSpecialInstallmentUser =
        currentUser?._id === "693a87b313df78d75f75c484";

    // Check if user has permission to approve/reject (superadmin, outright, and manager)
    const canApprove =
        superadmin || outright || manager || isSpecialInstallmentUser;

    // Determine principle parameter based on user type
    const getPrincipleParam = () => {
        if (superadmin) {
            // SuperAdmin can see all installment requests
            return undefined;
        } else if (outright || manager) {
            // Outright and manager users can only see their own principle
            return currentUser?._id;
        } else {
            // For other user types, use their principle if available
            return currentUser?.principle;
        }
    };

    useEffect(() => {
        if (canApprove) {
            fetchRequests();
        }
    }, [canApprove, currentUser?._id, currentUser?.principle, currentUser?.type, superadmin]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const principleParam = getPrincipleParam();
            let url = `/installmentRequests`;
            if (principleParam) {
                url += `?principle=${principleParam}`;
            }
            const res = await userRequest.get(url);
            setRequests(res.data);
        } catch (err) {
            console.error("Error fetching installment requests:", err);
            alert("Failed to fetch installment requests");
        } finally {
            setLoading(false);
        }
    };

    const openModal = (request) => {
        setSelectedRequest(request);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRequest(null);
    };

    const getStatusText = (status) => {
        const normalized = String(status || "pending").toLowerCase();
        if (normalized === "approved") return "Approved";
        if (normalized === "rejected") return "Rejected";
        return "Pending";
    };

    const formatCurrency = (amount) => {
        return `RM ${parseFloat(amount).toFixed(2)}`;
    };

    // Access control check AFTER all hooks
    if (!canApprove) {
        return (
            <div className="container mx-auto px-2 sm:px-4 py-8 block">
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-700">Only Superadmin, Outright, Manager, and authorized users can access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-2 sm:px-4 py-8 block">
            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto orders-table-wrapper">
                <table className="min-w-[1200px] divide-y divide-gray-200 orders-table w-full">
                    <thead className="bg-orange-400">
                        <tr>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Request Date
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Invoice ID
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Requested By
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan="6"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : requests.length === 0 ? (
                            <tr>
                                <td
                                    colSpan="6"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    No installment requests found.
                                </td>
                            </tr>
                        ) : (
                            requests.map((request) => (
                                <tr
                                    key={request._id}
                                    className="bg-green-200 hover:bg-green-100 transition-colors duration-200 cursor-pointer"
                                    onClick={() => openModal(request)}
                                >
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {moment(request.requestedDate).format("DD/MM/YYYY HH:mm")}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.orderId?.invoiceId || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.orderId?.userId?.name || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm font-semibold text-blue-600">
                                        {formatCurrency(request.amount)}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {request.requestedBy?.name || "Unknown"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                        <div className="flex items-center gap-2">
                                            <Eye
                                                className="pointer icon action-icon-view text-blue-500 hover:text-blue-700"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openModal(request);
                                                }}
                                                title="View Details"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal for detailed view */}
            {showModal && selectedRequest && (
                <div className="overlayContainer installment-modal">
                    <div className="center installment-modal__dialog">
                        <div className="close" onClick={closeModal}>
                            <Close />
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle text-xl font-semibold mb-4">
                                    Installment Request Details
                                </h3>
                                <div className="flex flex-col gap-4 mb-4">
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Status:</span>
                                        <span>{getStatusText(selectedRequest.status)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Request Date:</span>
                                        <span className="text-gray-900">{moment(selectedRequest.requestedDate).format("DD/MM/YYYY HH:mm:ss")}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Invoice ID:</span>
                                        <span className="text-gray-900">{selectedRequest.orderId?.invoiceId || "N/A"}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Customer Name:</span>
                                        <span className="text-gray-900">{selectedRequest.orderId?.userId?.name || "N/A"}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Installment Amount:</span>
                                        <span className="text-2xl font-bold text-blue-600">{formatCurrency(selectedRequest.amount)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Order Total:</span>
                                        <span className="text-gray-900">{formatCurrency(selectedRequest.orderId?.amount || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Order Deposit:</span>
                                        <span className="text-gray-900">{formatCurrency(selectedRequest.orderId?.deposit || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Total Deposited:</span>
                                        <span className="text-gray-900">{formatCurrency(selectedRequest.orderId?.totalDeposited || 0)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">Requested By:</span>
                                        <span className="text-gray-900">{selectedRequest.requestedBy?.name || "Unknown"}</span>
                                    </div>
                                    {selectedRequest.status === "approved" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">Approved By:</span>
                                                <span className="text-gray-900">{selectedRequest.approvedBy?.name || "Unknown"}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">Approved Date:</span>
                                                <span className="text-gray-900">{moment(selectedRequest.approvedDate).format("DD/MM/YYYY HH:mm:ss")}</span>
                                            </div>
                                        </>
                                    )}
                                    {selectedRequest.status === "rejected" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">Rejected By:</span>
                                                <span className="text-gray-900">{selectedRequest.rejectedBy?.name || "Unknown"}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">Rejected Date:</span>
                                                <span className="text-gray-900">{moment(selectedRequest.rejectedDate).format("DD/MM/YYYY HH:mm:ss")}</span>
                                            </div>
                                        </>
                                    )}
                                    {selectedRequest.remarks && (
                                        <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                            <span className="font-medium text-gray-700">Remarks:</span>
                                            <span className="text-gray-900">{selectedRequest.remarks}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallmentRequests;

